import { RequestContext } from 'context/RequestContext'
import Image from 'next/image'
import React, { useContext, useEffect, useRef, useState} from 'react'
import { openModal } from 'service/modal-service'
import { Services } from '@/index/service/Services'
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'framer-motion'
import { MdKeyboardDoubleArrowRight } from 'react-icons/md'
import { FreeConsultationGuide } from './FreeConsultationGuide'
import { personalTrainerPackages } from 'data/PersonalTrainerPackages'
import { TrainerSelectionGuide } from './TrainerSelectionGuide'
import { PersonalTrainerServiceDetails } from './PersonalTrainerServiceDetails'
import { HiSparkles } from 'react-icons/hi2'
import { FaWhatsapp } from 'react-icons/fa6'
import { FaAngleDown, FaStar } from 'react-icons/fa'
import { ShieldCheck } from 'lucide-react'
import { gbpUrl, googleRating, googleReviewCount, whatsAppDisplay, whatsAppUrl } from 'utils/business-info'
import { HeroTrainerStrip } from './HeroTrainerStrip'

// Lowest advertised per-session rate, derived from the package data so the hero
// price anchor cannot drift from the packages shown below it.
const lowestSessionPrice = Math.min(
    ...personalTrainerPackages.Signature.packages.map((pkg) => pkg.perSessionPrice)
)

// Optional props for the UAE / Malaysia pages (components/market/MarketPtHero);
// absent on the Singapore pages, which render exactly as before:
//   pills (trust pills after the rating), showRating, strip (replaces the
//   trainer strip), priceLabel (price anchor), whatsAppText, packagesHref.
export const PersonalTrainerHead = ({ heroImage, title, intro, heading, heroCopy, spList, trainerCount, seeAllHref, seeAllLabel, setIsQuickBook, openRequestForm, requestForm, setRequestForm, isQuickBook, isCustomBook, requestType, requestCategorySettings,
    pills, showRating = true, strip, priceLabel, whatsAppText = "I would like to request for a personal training consultation.", packagesHref = '#personal-training-packages' }) => {

    const [ isShowBottomButtons, setIsShowBottomButtons ] = useState(false)
    const [ isShowConsultationDetails, setIsShowConsultationDetails ] = useState(false);
    const [ isShowTrainerGuide, setIsShowTrainerGuide ] = useState(false)
    const [ isShowServiceDetails, setIsShowServiceDetails ] = useState(false);

    const {
        setRequestCriteria,
        setRequestDescription,
        setHeadline,
        setSelectedPackage,
        setServiceCost,
        setOriginalCost
    } = useContext(RequestContext);

    const openQuestionForm = () =>{
        setIsQuickBook(false)
        openRequestForm()
    }
    const confirmBook = ( serviceType, description) => {
        setRequestDescription(description)
        setIsQuickBook(true)
        openRequestForm(serviceType)
        setHeadline("Get my trainer & diet plan")
    }

    const quickBookPT = (serviceType, description) => {
        // Free consultation flow: clear any previously-selected package
        setSelectedPackage(null)
        setServiceCost(0)
        setOriginalCost(0)
        confirmBook( serviceType, description)
    }

    useEffect(() => {
        setRequestCriteria([])
        setRequestDescription('')
        setIsQuickBook(false)
    }, [setIsQuickBook, setRequestCriteria, setRequestDescription])

    const { scrollY } = useScroll()

    useMotionValueEvent(scrollY, "change", (latest) => {
        if(latest > 1000){
            if(!isShowBottomButtons){
                setIsShowBottomButtons(true)
            }
        } else {
            if(isShowBottomButtons){
                setIsShowBottomButtons(false)
            }
        }
    })

    return (
        <>
            {/* Content-sized hero: min-h guarantees presence on large screens while the
                section still grows with its content instead of clipping it.

                The fold used to open on the Signature/Elite package grid. Search intent
                for "personal trainer Singapore" is investigational - the SERP is
                listicles and directories, not single-brand booking pages - so the fold
                now leads with the trainer choice set and keeps only a one-line price
                anchor. Package selection was already handled further down the page by
                PersonalTrainerBooking (#personal-training-packages), which the scroll
                cue points at, so the grid here was redundant and has been removed. */}
            <section id="personal-trainer-booking" className="min-h-205 md:min-h-225 -mt-14 relative overflow-hidden bg-slate-900 flex items-center">
                <div className="absolute inset-0">
                    <Image className='bg-linear-to-br from-slate-800 to-orange-300'
                        src={heroImage}
                        fill
                        style={{ objectFit: 'cover' }}
                        alt={title}
                        priority={true}
                        sizes="(max-width: 768px) 100vw, 100vw"
                    />
                    {/* Dark overlay for dark theme consistency */}
                    <div className="absolute inset-0 bg-linear-to-t from-slate-900/90 via-slate-900/40 to-slate-900/20" />
                </div>
                <div id="campaign" className='relative z-10 w-full sm:bg-transparent md:py-16'>
                    {!requestForm ?
                        <div className='relative overflow-hidden bg-slate-900/70 backdrop-blur-md border border-white/10 rounded-4xl max-w-lg sm:max-w-4xl mx-auto px-4 py-10 space-y-5 text-center'>
                            {/* Mesh gradient accent */}
                            <div className="absolute top-0 left-1/4 w-48 h-48 bg-orange-500/20 rounded-full blur-3xl -translate-y-1/2 pointer-events-none" />
                            <div className="absolute bottom-0 right-1/4 w-40 h-40 bg-amber-400/15 rounded-full blur-3xl translate-y-1/2 pointer-events-none" />

                            <header className='space-y-3 relative z-10'>
                                {heading &&
                                    <h1 className='text-2xl sm:text-3xl font-extrabold text-white px-2 leading-tight tracking-tight'>
                                        {heading}
                                    </h1>
                                }
                                {intro &&
                                    <h3 className='text-lg text-white/80 px-3 leading-relaxed font-medium'>
                                        {intro}
                                    </h3>
                                }
                                {heroCopy &&
                                    <p className='text-sm text-white/70 px-3 leading-relaxed max-w-2xl mx-auto'>
                                        {heroCopy}
                                    </p>
                                }
                            </header>
                            {/* Trust signals - server rendered for crawlers.
                                Deliberately limited to claims not already shown lower in this
                                component (see Discount badge and Highlights rows below). */}
                            <div className='relative z-10 flex flex-wrap justify-center gap-2 text-xs sm:text-xs'>
                                {showRating &&
                                <a href={gbpUrl} target='_blank' rel='noreferrer'
                                    className='flex items-center gap-1 bg-white/90 text-gray-700 px-2.5 py-1 rounded-full border border-gray-200'>
                                    <FaStar className='text-amber-500' />
                                    <span className='font-semibold'>{googleRating}</span>
                                    <span className='text-gray-500'>Google ({googleReviewCount})</span>
                                </a>
                                }
                                {pills ? pills.map((pill) => (
                                    <span key={pill} className='bg-white/10 text-white/85 px-2.5 py-1 rounded-full border border-white/20'>{pill}</span>
                                )) : <>
                                <span className='bg-white/10 text-white/85 px-2.5 py-1 rounded-full border border-white/20'>Since 2018</span>
                                <span className='bg-white/10 text-white/85 px-2.5 py-1 rounded-full border border-white/20'>No gym membership</span>
                                </>}
                            </div>

                            {/* ─── Trainer choice set ─── */}
                            <div className='relative z-10 pt-1 sm:px-4'>
                                {strip ?? <HeroTrainerStrip spList={spList} trainerCount={trainerCount} seeAllHref={seeAllHref} seeAllLabel={seeAllLabel} />}
                            </div>

                            <div className='relative z-10 sm:px-4'>
                                <div className='h-px bg-white/10' />
                            </div>

                            {/* ─── Price anchor + primary CTA ─── */}
                            <div className='relative z-10 space-y-4 sm:px-4'>
                                <p className='text-sm text-white/80'>
                                    From <strong className='text-white text-base'>{priceLabel ?? <>${lowestSessionPrice}</>}</strong>/session &middot; no lock-in, no gym membership
                                </p>

                                <div className='flex justify-center'>
                                    <motion.button
                                        type="button"
                                        role="button"
                                        aria-label="Book free personal training trial"
                                        title="Book free personal training trial"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.97 }}
                                        onClick={() => quickBookPT("Fitness Trainer", "Personal training free consultation")}
                                        className="flex items-center justify-center text-lg sm:text-xl bg-linear-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 text-white font-bold px-8 py-4 rounded-full shadow-lg shadow-orange-500/30 transition-all duration-300"
                                    >
                                        <HiSparkles className='mr-2 text-2xl'/> Free Consultation
                                    </motion.button>
                                </div>

                                <button
                                    type="button"
                                    aria-label="View free consultation details"
                                    className='flex items-center justify-center w-full text-white/60 hover:text-white font-medium transition-colors'
                                    onClick={() => openModal(setIsShowConsultationDetails)}
                                >
                                    <MdKeyboardDoubleArrowRight className='text-2xl animate-pulse text-orange-400' />
                                    <span className='text-sm'>What&apos;s Included?</span>
                                </button>

                                {/* Points at PersonalTrainerBooking, which is the page's
                                    package selector (buddy toggle, peak/off-peak, savings). */}
                                <a href={packagesHref}
                                    className='flex items-center justify-center gap-1.5 text-xs font-medium text-white/60 hover:text-white transition-colors'>
                                    <span>See packages &amp; prices</span>
                                    <FaAngleDown />
                                </a>
                            </div>
                        </div>
                        :
                        <Services requestType={requestType} requestCategorySettings={requestCategorySettings} setRequestForm={setRequestForm} isQuickBook={isQuickBook}
                        isCustomBook={isCustomBook}/>
                    }
                </div>
            </section>


            <FreeConsultationGuide
                isOpen={isShowConsultationDetails}
                setIsShowConsultationDetails={setIsShowConsultationDetails}
                quickBookPT={quickBookPT}
            />
            <motion.div className='fixed bottom-2 w-full text-center z-20 flex justify-center space-x-5 text-sm'
                initial={{y: "50px"}}
                animate={isShowBottomButtons ? {y: "0px"} : {y: "80px"}}
                transition={{type: "spring", stiffness: 100, ease: "easeInOut", duration: 0.3}}
            >
                <button type="button" className='text-white py-3 px-6 rounded-full bg-linear-to-r from-orange-600 to-amber-400 shadow-lg ring-2 ring-orange-300 block font-bold transition'
                    onClick={() => quickBookPT("Fitness Trainer", "Personal training consultation")}
                    aria-label='Book Free Consultation for Personal Training'
                    >
                        Free Consultation
                </button>
                <a
                    href={`${whatsAppUrl}?text=${encodeURIComponent(whatsAppText)}`}
                    target="_blank"
                    rel="noreferrer"
                    className='flex items-center gap-2 text-white py-3 px-6 rounded-full bg-linear-to-r from-green-600 to-green-400 shadow-lg ring-2 ring-green-300 font-bold transition'
                    aria-label="Chat with us on WhatsApp"
                    title="Chat with us on WhatsApp">
                       <span>Chat </span>
                       <FaWhatsapp className='text-lg' />
                </a>
            </motion.div>
            {isShowTrainerGuide &&
                <TrainerSelectionGuide setIsShowTrainerGuide={setIsShowTrainerGuide} />
            }
            {isShowServiceDetails &&
                <AnimatePresence>
                    <PersonalTrainerServiceDetails setIsShowServiceDetails={setIsShowServiceDetails}/>
                </AnimatePresence>
            }
        </>
    )
}
